import React, { useState } from 'react';
import { toast } from 'react-toastify';
import '../styles/AddPipeline.css';
import { graphqlRequest } from '../utils/graphqlRequest';

export default function AddPipeline({ onClose, onSave }) {
  const [step, setStep] = useState(1); // Step 1 = Create Pipeline, Step 2 = Add Stages
  const [pipeline, setPipeline] = useState({
    name: '',
    order: '',
    description: '',
  });
  const [stages, setStages] = useState([{ name: '', order: '' }]);
  const [pipelineId, setPipelineId] = useState(null);
  const [loading, setLoading] = useState(false);
  // Handle pipeline input change
  const handlePipelineChange = (e) => {
    const { name, value } = e.target;
    setPipeline({ ...pipeline, [name]: value });
  };

  // Create pipeline mutation
  const handleCreatePipeline = async (e) => {
    e.preventDefault();
    if (!pipeline.name?.trim() || pipeline.order === '')
      return toast.error('Please enter a pipeline name and order!');

    setLoading(true);
    try {
      const query = `
        mutation CreatePipeline($input: CreatePipelineTypeInput!) {
          createPipeline(CreatePipelineTypeInput: $input) {
            id
            name
            order
            description
          }
        }
      `;

      const variables = {
        input: {
          name: pipeline.name.trim(),
          order: Number(pipeline.order),
          ...(pipeline.description?.trim()
            ? { description: pipeline.description.trim() }
            : {}),
        },
      };

      const data = await graphqlRequest(query, variables);
      const created = data?.createPipeline;

      if (created) {
        setPipelineId(created.id);
        if (typeof onSave === 'function') onSave(created);
        setStep(2);
      } else {
        toast.error('Failed to create pipeline!');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error creating pipeline!');
    }
    setLoading(false);
  };

  // Handle stage input changes
  const handleStageChange = (index, field, value) => {
    const updated = [...stages];
    updated[index][field] = value;
    setStages(updated);
  };

  // Add new stage
  const handleAddStage = () => {
    setStages([...stages, { name: '', order: '' }]);
  };

  // Create pipeline stages mutation
  // Create pipeline stages mutation
  const handleSaveStages = async (e) => {
    e.preventDefault();
    if (!pipelineId) return toast.error('Pipeline not found!');
    setLoading(true);

    try {
      const query = `
      mutation CreatePipelineStages($input: CreatePipelineStagesInput!) {
        createPipelineStages(createPipelineStageInput: $input) {
          id
          name
          order
          pipeline {
            id
            name
            description
            order
          }
        }
      }
    `;

      const variables = {
        input: {
          pipelineId,
          stages: stages.map((s) => ({
            name: s.name,
            order: Number(s.order),
          })),
        },
      };
      const data = await graphqlRequest(query, variables);

      if (data?.createPipelineStages) {
        toast.success('Pipeline and stages created successfully!');
        if (typeof onSave === 'function') onSave({ id: pipelineId, stages: true });
        onClose();
      } else {
        toast.error('Failed to create stages!');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error creating stages!');
    }
    setLoading(false);
  };

  return (
    <div className="add-pipeline-overlay">
      <div className="add-pipeline-container">
        {step === 1 ? (
          <>
            <h2 className="add-pipeline-title">Create New Pipeline</h2>
            <form onSubmit={handleCreatePipeline} className="add-pipeline-form">
              <label className="add-pipeline-label">Pipeline Name</label>
              <input
                type="text"
                name="name"
                value={pipeline.name}
                onChange={handlePipelineChange}
                placeholder="Enter pipeline name"
                className="add-pipeline-input"
              />

              <label className="add-pipeline-label">Order</label>
              <input
                type="number"
                name="order"
                value={pipeline.order}
                onChange={handlePipelineChange}
                placeholder="Enter order"
                className="add-pipeline-input"
              />

              <label className="add-pipeline-label">Description</label>
              <textarea
                name="description"
                value={pipeline.description}
                onChange={handlePipelineChange}
                placeholder="Enter pipeline description"
                className="add-pipeline-textarea"
              />

              <div className="add-pipeline-actions">
                <button
                  type="submit"
                  className="add-pipeline-save-btn"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Pipeline'}
                </button>
                <button
                  type="button"
                  className="add-pipeline-cancel-btn"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="add-pipeline-title">Add Stages</h2>
            <form onSubmit={handleSaveStages} className="add-pipeline-form">
              {stages.map((stage, index) => (
                <div className="section-card" key={index}>
                  <label className="add-pipeline-label">
                    Stage Name {index + 1}
                  </label>
                  <input
                    type="text"
                    value={stage.name}
                    onChange={(e) =>
                      handleStageChange(index, 'name', e.target.value)
                    }
                    placeholder="Enter stage name"
                    className="add-pipeline-input"
                  />
                  <label className="add-pipeline-label">Order</label>
                  <input
                    type="number"
                    value={stage.order}
                    onChange={(e) =>
                      handleStageChange(index, 'order', e.target.value)
                    }
                    placeholder="Enter stage order"
                    className="add-pipeline-input"
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddStage}
                className="add-pipeline-add-btn"
              >
                + Add Stage
              </button>

              <div className="add-pipeline-actions">
                <button
                  type="submit"
                  className="add-pipeline-save-btn"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Stages'}
                </button>
                <button
                  type="button"
                  className="add-pipeline-cancel-btn"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
